<template>
  <div
    class="row"
    v-if="!isDoubleSpecialMatchLayout">
    <div class="match">
      <div class="grid-left">
        <match-info :match-id="matchId" />
      </div>
      <div class="grid-right">
        <transition name="slide-locked">
          <div
            class="match-bets"
            :key="1"
            v-if="matchBettingStatus">
            <match-bet
              v-for="matchBet in matchBets"
              :key="matchBet.id"
              :match-bet="matchBet"
            />
          </div>
          <match-locked
            :match-status-period="matchStatus.period"
            :key="2"
            v-else
          />
        </transition>
      </div>
    </div>
  </div>
  <div
    class="row row-double"
    v-else>
    <div class="grid-left">
      <div class="match">
        <match-info-double :match-id="matchId" />
      </div>
    </div>
    <div class="grid-right">
      <div class="match">
        <transition name="slide-locked">
          <div
            class="match-bets"
            :key="1"
            v-if="matchBettingStatus">
            <match-bet
              :class="{ 'bets-colored-italic': index > 4 }"
              v-for="(matchBet, index) in matchBets"
              :key="matchBet.id"
              :match-bet="matchBet"
            />
          </div>
          <match-locked
            :match-status-period="matchStatus.period"
            :key="2"
            v-else
          />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { each, map, find } from 'lodash';
import MatchLocked from '@/components/common/match-locked/MatchLocked.vue';
import MatchInfoDouble from './MatchInfoDouble.vue';
import MatchInfo from './MatchInfo.vue';
import MatchBet from './MatchBet.vue';

const getSportBets = ({ type, sportBets }) => {
  const parsedSportBets = [];

  switch (type) {
    case 'double-1':
      each(sportBets, (sportBet) => {
        parsedSportBets.push(sportBet.bets[0]);
      });
      break;
    case 'double-2':
      each(sportBets, (sportBet, i) => {
        parsedSportBets[i] = sportBet.bets[0] || {};
        parsedSportBets[i + 5] = sportBet.bets[1] || {}; // Period bet
      });
      break;
    default:
  }

  return parsedSportBets;
};

export default {
  name: 'Match',
  components: {
    MatchInfoDouble,
    MatchInfo,
    MatchBet,
    MatchLocked,
  },
  props: {
    matchId: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      idSport: this.$store.getters.getSportId(this.matchId),
    };
  },
  computed: {
    matchBettingStatus() {
      return this.$store.getters.getMatchBettingStatus(this.matchId);
    },
    matchStatus() {
      return this.$store.getters.getMatchStatus(this.matchId);
    },
    matchTemplateInfo() {
      return this.$store.getters.getMatchTemplateInfo(this.matchId);
    },
    isDoubleSpecialMatchLayout() {
      return this.matchTemplateInfo && this.matchStatus.isFirstPeriod;
    },
    matchBets() {
      let type;
      if (this.isDoubleSpecialMatchLayout) type = 'double-2';
      else type = 'double-1';

      const sportBets = getSportBets({
        type,
        sportBets: this.$store.getters.sportBets(this.idSport),
      });

      const parsedMatchBets = map(sportBets, (sportBet) => {
        const matchBet = find(this.$store.getters.matchBets(this.matchId),
          mb => mb.active && mb.idBet === sportBet.id && mb.mostBalanced);

        if (matchBet) {
          const mbOutcomes = [];
          each(sportBet.outcomes, (sbo) => {
            const mbOutcome = find(matchBet.outcomes, mbo => mbo.active && sbo.id === mbo.idBo);
            mbOutcomes.push(mbOutcome || {});
          });

          // Add special value
          if (matchBet.special) {
            mbOutcomes.splice(1, 0, {
              valueSpecial: +matchBet.special,
            });
          }

          matchBet.outcomes = mbOutcomes;
        }

        return matchBet || {};
      });

      return parsedMatchBets;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/style/variables";

.row-double {
  height: 10vh;
  .grid-left,
  .grid-right {
    height: 100%;
  }
  .match-bet {
    height: 50%;
    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(3),
    &:nth-child(4),
    &:nth-child(5) {
      border-bottom: $match-bet-vertical-line-width solid
        $match-bet-vertical-line-color-back;
    }
  }
}

.row .match {
  height: 100%;
  color: $color-text-primary;
  background-color: $match-color-back;

  /deep/ {
    .card {
      border-color: $match-color-back;
    }

    .border-cover {
      background-color: $match-id-color-back;
    }

    .match-name:before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      content: "";
      width: 20px;
      background: linear-gradient(to right, rgba($match-color-back, 0), $match-color-back);
      z-index: 1;
    }
  }
}

.row:nth-child(odd) .match {
  background-color: $match-color-back-even;

  /deep/ {
    .card {
      border-color: $match-color-back-even;
    }

    .border-cover {
      background-color: $match-id-even-color-back;
    }

    .match-display-id {
      background-color: $match-id-even-color-back;
    }

    .match-name:before {
      background: linear-gradient(to right,
        rgba($match-color-back-even, 0), $match-color-back-even);
    }
  }
}

.match-bets {
  height: 100%;
}

.locked {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  font-size: 0.625rem;
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: $match-bet-vertical-line-width;
    background-color: $match-bet-vertical-line-color-back;
  }

  i {
    padding-right: 10px;
  }
}

.period-name {
  float: right;
  font-size: 0.8125rem;
  padding-right: 20px;
}

.bets-colored-italic {
  color: $color-important !important;
  font-style: italic;
}
</style>
